import { useEffect, useState } from 'react'
import { Plus, AlertTriangle, CheckCircle, Pencil, Trash2 } from 'lucide-react'
import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'
import { fmtDate, fmtEuro, hoje, periodoAtual } from '../utils/fmt'

const emptyForm = { tipo: 'CREDITO', categoria: 'PAGAMENTO', descricao: '', valor: '', data: hoje(), periodo: '' }

export default function ContasCorrentes() {
  const { condominioAtivo } = useAppStore()
  const [fracoes, setFracoes] = useState([])
  const [totalDivida, setTotalDivida] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [movimentos, setMovimentos] = useState(null)
  const [loadingCC, setLoadingCC] = useState(false)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')

  const load = (autoSelect = false) => {
    if (!condominioAtivo) return
    setLoading(true)
    api.get(`/cc/condominio/${condominioAtivo.id}`)
      .then(r => { 
      const lista = r.data.fracoes || []
      setFracoes(lista)
      setTotalDivida(r.data.totalDivida || 0)
      if (autoSelect && lista.length > 0) openCC(lista[0])
    })
      .finally(() => setLoading(false))
  }

  useEffect(() => { load(true) }, [condominioAtivo])

  const openCC = async (fracao) => {
    setSelected(fracao)
    setLoadingCC(true)
    try {
      const { data } = await api.get(`/cc/${fracao.fracaoId}`)
      setMovimentos(data)
    } finally { setLoadingCC(false) }
  }

  const openCreate = () => { setForm(emptyForm); setEditId(null); setErro(''); setModal(true) }
  const openEdit = (m) => {
    setForm({
      tipo: m.tipo, categoria: m.categoria, descricao: m.descricao,
      valor: m.valor, data: new Date(m.data).toISOString().split('T')[0],
      periodo: m.periodo || ''
    })
    setEditId(m.id); setErro(''); setModal(true)
  }

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    try {
      if (editId) {
        await api.put(`/cc/movimento/${editId}`, { ...form, valor: parseFloat(form.valor) })
      } else {
        await api.post(`/cc/${selected.fracaoId}/movimento`, { ...form, valor: parseFloat(form.valor) })
      }
      setModal(false)
      openCC(selected)
      load()
    } catch (err) { setErro(err.response?.data?.erro || 'Erro.') }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este movimento?')) return
    await api.delete(`/cc/movimento/${id}`)
    openCC(selected)
    load()
  }

  return (
    <>
      <Topbar title="Contas Correntes" />
      <div className="page-content">
        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16 }}>
          <div>
            <div className="page-header">
              <h1>Contas correntes</h1>
              <p>Dívida total: <strong style={{ color: totalDivida > 0 ? 'var(--red-dark)' : 'var(--green-dark)' }}>{fmtEuro(totalDivida)}</strong></p>
            </div>
            <div className="card" style={{ padding: 0 }}>
              {loading ? <div className="loading-center"><span className="spinner" /></div> : fracoes.map(f => (
                <div key={f.fracaoId} onClick={() => openCC(f)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  background: selected?.fracaoId === f.fracaoId ? 'var(--green-light)' : 'transparent'
                }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: f.estado === 'REGULARIZADO' ? 'var(--green-light)' : 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: f.estado === 'REGULARIZADO' ? 'var(--green-dark)' : 'var(--red-dark)', flexShrink: 0 }}>
                    {f.designacao}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.condominos?.[0] || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Quota: {fmtEuro(f.valorQuota)}/mês</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {f.estado === 'REGULARIZADO'
                      ? <span className="badge badge-green"><CheckCircle size={9} style={{ marginRight: 3 }} />OK</span>
                      : <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red-dark)' }}>{fmtEuro(f.totalDivida)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{f.mesesEmDivida} meses</div>
                        </div>
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selected ? (
            <div>
              <div className="page-header">
                <div className="page-header-row">
                  <div>
                    <h1>Fração {selected.designacao}</h1>
                    <p>{selected.condominos?.[0] || '—'} · Quota: {fmtEuro(selected.valorQuota)}/mês</p>
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={openCreate}><Plus size={13} /> Movimento</button>
                </div>
              </div>
              <div className="card">
                {loadingCC ? <div className="loading-center"><span className="spinner" /></div> : movimentos && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                      <div style={{ padding: 12, background: movimentos.totalDivida > 0 ? 'var(--red-light)' : 'var(--green-light)', borderRadius: 'var(--radius)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: movimentos.totalDivida > 0 ? 'var(--red-dark)' : 'var(--green-dark)' }}>
                          {movimentos.totalDivida > 0 ? 'Em dívida' : 'Regularizado'}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: movimentos.totalDivida > 0 ? 'var(--red-dark)' : 'var(--green-dark)' }}>{fmtEuro(movimentos.totalDivida)}</div>
                        {movimentos.mesesEmDivida > 0 && <div style={{ fontSize: 11, color: 'var(--red-dark)' }}>{movimentos.mesesEmDivida} meses em falta</div>}
                      </div>
                      <div style={{ padding: 12, background: 'var(--green-light)', borderRadius: 'var(--radius)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--green-dark)' }}>Quotas pagas</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--green-dark)' }}>{fmtEuro(movimentos.totalCreditos)}</div>
                      </div>
                      <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Outros débitos</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtEuro(movimentos.totalDebitos)}</div>
                      </div>
                    </div>

                    {movimentos.periodosEmDivida?.length > 0 && (
                      <div style={{ padding: 10, background: 'var(--red-light)', borderRadius: 'var(--radius)', marginBottom: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--red-dark)', marginBottom: 6 }}>
                          <AlertTriangle size={12} style={{ marginRight: 4, display: 'inline' }} />
                          Meses sem quota lançada:
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {movimentos.periodosEmDivida.map(p => <span key={p} className="badge badge-red">{p}</span>)}
                        </div>
                      </div>
                    )}

                    <div className="table-wrap">
                      <table className="data-table">
                        <thead><tr><th>Data</th><th>Descrição</th><th>Período</th><th>Tipo</th><th>Valor</th><th></th></tr></thead>
                        <tbody>
                          {movimentos.movimentos?.length === 0
                            ? <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}>Sem movimentos.</td></tr>
                            : movimentos.movimentos?.map(m => (
                              <tr key={m.id}>
                                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(m.data)}</td>
                                <td style={{ fontSize: 12 }}>{m.descricao}</td>
                                <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{m.periodo || '—'}</td>
                                <td><span className={`badge badge-${m.tipo === 'CREDITO' ? 'green' : 'red'}`}>{m.tipo === 'CREDITO' ? 'Pago' : 'Débito'}</span></td>
                                <td style={{ fontWeight: 600, color: m.tipo === 'CREDITO' ? 'var(--green-dark)' : 'var(--red-dark)' }}>
                                  {m.tipo === 'CREDITO' ? '+' : '-'}{fmtEuro(m.valor)}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <button className="btn btn-sm" onClick={() => openEdit(m)} title="Editar"><Pencil size={11} /></button>
                                    <button className="btn btn-sm" style={{ color: 'var(--red-dark)' }} onClick={() => handleDelete(m.id)} title="Eliminar"><Trash2 size={11} /></button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)", fontSize: 13 }}>
              Seleciona uma fração à esquerda
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Editar movimento' : `Novo movimento — Fração ${selected?.designacao}`}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleSave}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                    <option value="CREDITO">Crédito (recebido)</option>
                    <option value="DEBITO">Débito (encargo)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Categoria</label>
                  <select className="form-select" value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>
                    <option value="PAGAMENTO">Pagamento</option>
                    <option value="QUOTA">Quota</option>
                    <option value="FUNDO_RESERVA">Fundo reserva</option>
                    <option value="OBRA">Obra extraordinária</option>
                    <option value="PENALIZACAO">Penalização</option>
                    <option value="ACERTO">Acerto</option>
                    <option value="DEVOLUCAO">Devolução</option>
                    <option value="OUTRO">Outro</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Descrição *</label>
                <input className="form-input" value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Valor (€) *</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={form.valor} onChange={e => setForm({ ...form, valor: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Data *</label>
                  <input className="form-input" type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Período (ex: 2025-07)</label>
                <input className="form-input" placeholder="AAAA-MM" value={form.periodo} onChange={e => setForm({ ...form, periodo: e.target.value })} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : editId ? 'Guardar' : 'Lançar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
